/**
 * 인게임 맵 에디터 — **게리모드의 물리건에 해당한다.**
 *
 * Wayne 의 요청: 「게임 속으로 들어가서 맵을 편집할 수 있는 모드. 원하는 물건을 집어서
 * 움직이거나 회전한다거나」.
 *
 * ## 왜 이렇게 만들었나
 *
 * 레벨의 소스는 **코드**(`shared/level-*.ts`)다. 그래서 이 에디터는 씬을 저장하지 않는다 —
 * 옮긴 결과를 **TS 코드 조각으로 내보낸다.** 그것을 PIECES 배열에 붙여넣으면 영구가 된다.
 * 씬을 따로 저장하면 코드와 씬이 서로 다른 진실을 갖게 되고, 단서 좌표가 코드에 박혀 있는
 * 이 게임에서는 그게 곧 버그다.
 *
 * 편집 대상은 **PIECES(카탈로그 조각)뿐**이다:
 *   - FURNITURE 는 StaticBatcher 가 재질별로 병합해서 개별 물체가 씬에 남지 않는다
 *   - PROPS 는 서버가 상태를 갖는다(열림·발견 여부) — 클라이언트가 함부로 옮기면 어긋난다
 * 조각은 각자 group 으로 있고 world.ts 가 `userData.pieceId` 를 심어 둔다.
 *
 * ## 조작
 *
 *   `  (백틱)     편집 모드 켜기/끄기
 *   왼쪽 클릭     조준한 조각을 집는다 / 놓는다
 *   마우스 이동   집은 것을 시선 앞에서 끌고 다닌다
 *   휠            거리 조절 (집은 상태)
 *   Q / E         Y축 회전 (15도, Shift 를 누르면 1도)
 *   Z / X         높이 조절 (5cm, Shift 로 5mm)
 *   G             바닥에 내려놓기 (y=0)
 *   Delete        이 조각을 숨긴다(코드에서 지우려는 표시)
 *   Ctrl+C        **바뀐 것만** TS 코드로 클립보드에 복사
 */
import * as THREE from 'three';

export interface EditTarget {
  group: THREE.Object3D;
  pieceId: string;
  room: string;
  /** 처음 좌표 — 바뀐 것만 내보내려면 원본을 알아야 한다 */
  home: { pos: [number, number, number]; rot: number };
}

const SNAP_ROT = Math.PI / 12; // 15도
const FINE_ROT = Math.PI / 180; // 1도
const SNAP_Y = 0.05;
const FINE_Y = 0.005;

export class MapEditor {
  on = false;
  private held: EditTarget | null = null;
  private holdDist = 2.2;
  private targets: EditTarget[] = [];
  private removed = new Set<THREE.Object3D>();
  private ray = new THREE.Raycaster();

  constructor(
    private root: THREE.Object3D,
    private camera: THREE.Camera,
    private say: (msg: string) => void,
  ) {
    this.ray.far = 12;
  }

  /** 씬을 훑어 조각 목록을 만든다. 편집 모드를 켤 때 한 번 */
  private collect() {
    if (this.targets.length) return;
    this.root.traverse((o) => {
      const id = o.userData?.pieceId as string | undefined;
      if (!id) return;
      this.targets.push({
        group: o,
        pieceId: id,
        room: (o.userData.pieceRoom as string) ?? '',
        home: {
          pos: [
            +o.position.x.toFixed(3),
            +o.position.y.toFixed(3),
            +o.position.z.toFixed(3),
          ],
          rot: +o.rotation.y.toFixed(4),
        },
      });
    });
  }

  toggle() {
    this.on = !this.on;
    if (this.on) {
      this.collect();
      this.say(`편집 모드 — 조각 ${this.targets.length}개. 클릭으로 집고 Ctrl+C 로 코드 복사`);
    } else {
      this.drop();
      this.say('편집 모드 해제');
    }
  }

  /** 조준한 조각을 찾는다. 조각은 자식 메시로 맞으므로 부모를 거슬러 올라간다 */
  private aim(): EditTarget | null {
    this.ray.setFromCamera(new THREE.Vector2(0, 0), this.camera);
    const hits = this.ray.intersectObjects(
      this.targets.map((t) => t.group),
      true,
    );
    for (const h of hits) {
      for (let o: THREE.Object3D | null = h.object; o; o = o.parent) {
        const t = this.targets.find((x) => x.group === o);
        if (t && !this.removed.has(t.group)) return t;
      }
    }
    return null;
  }

  /** 왼쪽 클릭 — 집거나 놓는다. 편집 모드가 아니면 false 를 돌려 원래 동작을 살린다 */
  click(): boolean {
    if (!this.on) return false;
    if (this.held) {
      this.drop();
      return true;
    }
    const t = this.aim();
    if (!t) {
      this.say('집을 조각이 없다 — 조각을 바라보고 클릭할 것');
      return true;
    }
    this.held = t;
    this.holdDist = this.camera.position.distanceTo(t.group.position);
    this.holdDist = Math.max(1.0, Math.min(6, this.holdDist));
    this.say(`${t.pieceId} 집음 — Q/E 회전 · Z/X 높이 · G 바닥 · 클릭으로 놓기`);
    return true;
  }

  private drop() {
    if (!this.held) return;
    const p = this.held.group.position;
    this.say(`${this.held.pieceId} 놓음 [${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)}]`);
    this.held = null;
  }

  /** 매 프레임 — 집은 것을 시선 앞에 붙여 둔다 */
  update() {
    if (!this.on || !this.held) return;
    const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    const at = this.camera.position.clone().add(dir.multiplyScalar(this.holdDist));
    // **y 는 건드리지 않는다.** 조각 원점은 접지면이라 바닥 높이를 유지하는 것이 기본이고,
    // 높이는 Z/X 로만 바꾼다 — 마우스로 y 까지 끌면 물건이 늘 공중에 뜬다.
    this.held.group.position.x = at.x;
    this.held.group.position.z = at.z;
  }

  wheel(deltaY: number): boolean {
    if (!this.on || !this.held) return false;
    this.holdDist = Math.max(0.8, Math.min(7, this.holdDist + (deltaY > 0 ? 0.25 : -0.25)));
    return true;
  }

  /** 키 처리. 처리했으면 true */
  key(code: string, shift: boolean, ctrl: boolean): boolean {
    // **편집 모드가 아니면 아무 키도 먹지 않는다.** 여기서 먼저 걸러야 Ctrl+C·E·G·Q 가
    // 평소의 게임 조작으로 남는다.
    if (!this.on) return false;
    if (ctrl && code === 'KeyC') {
      this.copy();
      return true;
    }
    const t = this.held ?? this.aim();
    if (!t) return false;

    const rStep = shift ? FINE_ROT : SNAP_ROT;
    const yStep = shift ? FINE_Y : SNAP_Y;
    switch (code) {
      case 'KeyQ':
        t.group.rotation.y += rStep;
        return true;
      case 'KeyE':
        t.group.rotation.y -= rStep;
        return true;
      case 'KeyZ':
        t.group.position.y = Math.max(0, t.group.position.y - yStep);
        return true;
      case 'KeyX':
        t.group.position.y += yStep;
        return true;
      case 'KeyG':
        t.group.position.y = 0;
        this.say(`${t.pieceId} 바닥에 내려놓음`);
        return true;
      case 'Delete':
      case 'Backspace':
        t.group.visible = false;
        this.removed.add(t.group);
        if (this.held === t) this.held = null;
        this.say(`${t.pieceId} 숨김 — Ctrl+C 하면 「지울 것」으로 표시된다`);
        return true;
      default:
        return false;
    }
  }

  /**
   * **바뀐 것만** TS 코드로 내보낸다.
   *
   * 전부 내보내면 25줄이 쏟아져 무엇이 바뀌었는지 안 보이고, 붙여넣을 때 손으로 쓴 주석이
   * 날아간다. 원래 좌표(home)와 다른 것만 낸다.
   */
  private copy() {
    this.collect();
    const lines: string[] = [];
    for (const t of this.targets) {
      if (this.removed.has(t.group)) {
        lines.push(`  // 지울 것: { piece: '${t.pieceId}', room: '${t.room}', … }`);
        continue;
      }
      const p = t.group.position;
      const rot = t.group.rotation.y;
      const moved =
        Math.abs(p.x - t.home.pos[0]) > 0.0005 ||
        Math.abs(p.y - t.home.pos[1]) > 0.0005 ||
        Math.abs(p.z - t.home.pos[2]) > 0.0005 ||
        Math.abs(rot - t.home.rot) > 0.0005;
      if (!moved) continue;
      const rs = Math.abs(rot) < 0.0005 ? '' : `, rot: ${rot.toFixed(4)}`;
      lines.push(
        `  { piece: '${t.pieceId}', room: '${t.room}', ` +
          `pos: [${p.x.toFixed(2)}, ${p.y.toFixed(3)}, ${p.z.toFixed(2)}]${rs} },`,
      );
    }
    if (!lines.length) {
      this.say('바뀐 조각이 없다');
      return;
    }
    const out = `// 인게임 에디터(F7)가 낸 좌표 — shared/level-sahol.ts 의 PIECES 에 반영할 것\n${lines.join('\n')}`;
    navigator.clipboard?.writeText(out).then(
      () => this.say(`${lines.length}줄 클립보드에 복사했다`),
      () => {
        // 클립보드가 막힌 환경(비 HTTPS 등)에서는 콘솔로 낸다
        console.log(out);
        this.say(`클립보드 실패 — 콘솔에 냈다 (${lines.length}줄)`);
      },
    );
  }

  /** HUD 에 뭘 조준했는지 보여 준다 */
  aimedLabel(): string | null {
    if (!this.on) return null;
    if (this.held) {
      const p = this.held.group.position;
      return `✋ ${this.held.pieceId} [${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)}] ${((this.held.group.rotation.y * 180) / Math.PI).toFixed(0)}°`;
    }
    const t = this.aim();
    return t ? `◎ ${t.pieceId}` : '◎ —';
  }
}
